import {
  AggregationIcon,
  AgGridHeaderGroupInnerHeaderRoot,
  AgGridHeaderGroupMenuButton,
  Button,
  ButtonVariants,
  COLUMN_GROUP_NATIVE_TYPE,
  FormControlSizes,
  HStack,
  showAgGridColumnMenu,
  Text,
  Tooltip,
  type Column,
  type ColumnGroup,
} from '@talos/kyoko';
import type { Column as AgGridColumn, IHeaderGroupParams } from 'ag-grid-community';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { isPriceLadderMode } from '../ladder';
import { getPriceGridLegMarketSelectorColumn } from './priceGridLegMarketSelectorColumn';
import {
  getPriceGridMarketDataColumnGroupId,
  getPriceGridMarketDataColumnTitle,
  PRICE_GRID_MARKET_DATA_PRICE_COLUMN_SUFFIX,
  type PriceGridMarketDataColumnRow,
} from './priceGridMarketDataColumnUtils';
import { getPriceGridMarketDataPriceColumn } from './priceGridMarketDataPriceColumn';
import {
  getPriceGridMarketDataBidQtyColumn,
  getPriceGridMarketDataOfferQtyColumn,
  type PriceGridLadderColumnConfig,
} from './priceGridMarketDataQtyColumns';
import { getPriceGridMarketStatusColumn } from './priceGridMarketStatusColumn';
import { getPriceGridRFQColumn } from './priceGridRFQColumn';
import { getPriceGridSymbolMenuColumn } from './priceGridSymbolMenuColumn';
import type { PriceGridSecurityMarketDataColumnParams } from './types';

const PriceGridMarketDataGroupMenuButton = styled(AgGridHeaderGroupMenuButton).attrs({
  className: 'price-grid-market-data-group-menu-button',
  dataTestId: 'price-grid-market-data-group-menu-button',
})``;

export function getPriceGridMarketDataMenuLeafColumn(
  columnGroup: IHeaderGroupParams['columnGroup']
): AgGridColumn | undefined {
  const leafColumns = columnGroup.getDisplayedLeafColumns();
  const priceLeaf = leafColumns.find(column =>
    column.getColId().endsWith(`-${PRICE_GRID_MARKET_DATA_PRICE_COLUMN_SUFFIX}`)
  );
  return priceLeaf ?? leafColumns[0];
}

export function PriceGridMarketDataColumnGroupHeader(
  params: IHeaderGroupParams & {
    settingsButton: {
      label: string;
      tooltip?: string;
      showAggregationIcon?: boolean;
      onClick: (params: IHeaderGroupParams) => void;
    };
  }
) {
  const { displayName, settingsButton } = params;
  const headerRootRef = useRef<HTMLDivElement>(null);
  const menuColumn = getPriceGridMarketDataMenuLeafColumn(params.columnGroup);
  const getHeaderGroupCell = () => headerRootRef.current?.closest('.ag-header-group-cell') ?? null;

  useEffect(() => {
    const headerCell = headerRootRef.current?.closest('.ag-header-group-cell');
    const resizeHandle = headerCell?.querySelector<HTMLElement>(':scope > .ag-header-cell-resize');
    if (!resizeHandle) {
      return;
    }

    const startResize = (event: globalThis.MouseEvent) => {
      event.preventDefault();
      event.stopImmediatePropagation();

      const resizableLeaves = params.columnGroup
        .getDisplayedLeafColumns()
        .filter(column => column.getColDef().resizable !== false);
      const initialWidths = resizableLeaves.map(column => ({
        column,
        width: column.getActualWidth(),
      }));
      const initialTotalWidth = initialWidths.reduce((total, { width }) => total + width, 0);
      if (initialTotalWidth === 0) {
        return;
      }

      const startX = event.clientX;
      const minTotalWidth = initialWidths.reduce((total, { column }) => total + column.getMinWidth(), 0);

      const resize = (moveEvent: globalThis.MouseEvent, finished: boolean) => {
        const nextTotalWidth = Math.max(minTotalWidth, initialTotalWidth + moveEvent.clientX - startX);
        params.api.setColumnWidths(
          initialWidths.map(({ column, width }) => ({
            key: column,
            newWidth: (width / initialTotalWidth) * nextTotalWidth,
          })),
          finished,
          'uiColumnResized'
        );
      };

      const handleResize = (moveEvent: globalThis.MouseEvent) => resize(moveEvent, false);
      const finishResize = (upEvent: globalThis.MouseEvent) => {
        resize(upEvent, true);
        document.removeEventListener('mousemove', handleResize);
        document.removeEventListener('mouseup', finishResize);
      };

      document.addEventListener('mousemove', handleResize);
      document.addEventListener('mouseup', finishResize);
    };

    resizeHandle.addEventListener('mousedown', startResize, { capture: true });
    return () => resizeHandle.removeEventListener('mousedown', startResize, { capture: true });
  }, [params.api, params.columnGroup]);

  const settingsControl = (
    <Button
      size={FormControlSizes.Tiny}
      variant={ButtonVariants.Default}
      onClick={event => {
        event.stopPropagation();
        settingsButton.onClick(params);
      }}
      data-testid="price-grid-price-column-settings-button"
    >
      {settingsButton.showAggregationIcon ? (
        <AggregationIcon data-testid="price-grid-price-column-aggregation-icon" />
      ) : null}
      {settingsButton.label}
    </Button>
  );

  useEffect(() => {
    const headerGroupCell = params.eGridHeader;
    if (!menuColumn) {
      return;
    }

    const handleContextMenu = (event: Event) => {
      if (!(event instanceof MouseEvent)) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      // Anchored open via private menuSvc (see showAgGridColumnMenu.ts) — public showColumnMenu
      // cannot position against the group header / mouse event.
      showAgGridColumnMenu(params.api, menuColumn, {
        fallbackElement: headerGroupCell,
        mouseEvent: event,
        positionBy: 'mouse',
      });
    };

    headerGroupCell.addEventListener('contextmenu', handleContextMenu, { capture: true });
    return () => headerGroupCell.removeEventListener('contextmenu', handleContextMenu, { capture: true });
  }, [menuColumn, params.api, params.eGridHeader]);

  return (
    <AgGridHeaderGroupInnerHeaderRoot
      ref={headerRootRef}
      className="ag-header-group-inner-header-root"
      w="100%"
      gap="spacing4"
      justifyContent="flex-start"
    >
      <Text>{displayName}</Text>
      <HStack gap="spacing4" alignItems="center" ml="auto">
        {settingsButton.tooltip ? (
          <Tooltip
            tooltip={settingsButton.tooltip}
            placement="top-start"
            tooltipTestID="price-grid-price-column-markets-tooltip"
          >
            {settingsControl}
          </Tooltip>
        ) : (
          settingsControl
        )}
        {menuColumn ? (
          <PriceGridMarketDataGroupMenuButton
            api={params.api}
            menuColumn={menuColumn}
            ariaLabel="Open price column menu"
            getHeaderGroupCell={getHeaderGroupCell}
          />
        ) : null}
      </HStack>
    </AgGridHeaderGroupInnerHeaderRoot>
  );
}

export function getPriceGridMarketDataColumnGroup({
  column,
  includeActionColumns = true,
  includeLegMarketSelectorColumn = false,
  includeRFQColumn = true,
  isBBOEnabled,
  ladderConfig,
}: {
  column: Column<PriceGridSecurityMarketDataColumnParams, PriceGridMarketDataColumnRow>;
  includeActionColumns?: boolean;
  includeLegMarketSelectorColumn?: boolean;
  includeRFQColumn?: boolean;
  isBBOEnabled: boolean;
  ladderConfig: PriceGridLadderColumnConfig;
}): ColumnGroup {
  const groupId = getPriceGridMarketDataColumnGroupId(column);
  const title = getPriceGridMarketDataColumnTitle(column);
  const priceColumn = getPriceGridMarketDataPriceColumn(column, { isBBOEnabled, ladderConfig });
  const priceModeChildren = isPriceLadderMode(ladderConfig.ladderType)
    ? [
        getPriceGridMarketDataBidQtyColumn(column, ladderConfig),
        priceColumn,
        getPriceGridMarketDataOfferQtyColumn(column, ladderConfig),
      ]
    : [priceColumn];

  return {
    type: COLUMN_GROUP_NATIVE_TYPE,
    groupId,
    headerName: title,
    headerValueGetter: () => title,
    suppressColumnsToolPanel: true,
    marryChildren: true,
    children: includeActionColumns
      ? [
          ...priceModeChildren,
          ...(includeRFQColumn ? [getPriceGridRFQColumn(column)] : []),
          ...(includeLegMarketSelectorColumn ? [getPriceGridLegMarketSelectorColumn(column)] : []),
          getPriceGridMarketStatusColumn(column),
          getPriceGridSymbolMenuColumn(column),
        ]
      : priceModeChildren,
  };
}
